import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Skeleton,
} from "@mui/material";
import {
  Refresh,
  Download,
  WarningAmber,
  Public,
  FilterList,
  CheckCircle,
  ErrorOutline,
  TrendingUp,
  TrendingDown,
  PersonOutline,
  AirplanemodeActive,
  CalendarToday,
  LocalHospital,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart as ReBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from "recharts";
import { format } from "date-fns";
import { fr as frLocale, enUS as enLocale } from "date-fns/locale";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { reportsAPI } from "../services/api";
import GeoDashboardMap from "../components/Dashboard/GeoDashboardMap";
import PortEntryMap from "../components/Dashboard/PortEntryMap";
import { F, useDashTokens, riskClr, riskBgClr, DashTokens } from "../theme/dashTokens";

/* ═══════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — per-mode tokens live in theme/dashTokens.ts;
   components read them via useDashTokens()
══════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════
   DATA CONFIG
══════════════════════════════════════════════════════════════════ */
const KPI_CONFIG = [
  { labelKey: "ebolaDashboard.kpi.totalScreenings", key: "total_screenings", icon: PersonOutline,      colorKey: "blue"  as keyof DashTokens, cumulative_key: "total_screenings_cumulative" },
  { labelKey: "ebolaDashboard.kpi.clearedPassed",   key: "cleared",          icon: CheckCircle,        colorKey: "green" as keyof DashTokens, cumulative_key: "cleared_cumulative",   pct_key: "cleared_pct" },
  { labelKey: "ebolaDashboard.kpi.highRiskAlerts",  key: "high_risk",        icon: WarningAmber,       colorKey: "red"   as keyof DashTokens, cumulative_key: "high_risk_cumulative", pct_key: "high_risk_pct" },
  { labelKey: "ebolaDashboard.kpi.pointOfEntry",    key: "active_ports",     icon: AirplanemodeActive, colorKey: "muted" as keyof DashTokens },
];

const numFmt = (v: number | undefined | null) => (v ?? 0).toLocaleString();

// Mount stagger: fade-up entrance for page sections
const rise = (i: number) => ({
  animation: "dashRise .45s ease both",
  animationDelay: `${i * 70}ms`,
  "@keyframes dashRise": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to:   { opacity: 1, transform: "none" },
  },
});

/* ═══════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════════════════════════════ */

const LivePulse: React.FC<{ color?: string }> = ({ color }) => {
  const { C } = useDashTokens();
  const dot = color ?? C.green;
  return (
    <Box sx={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}>
      <Box sx={{
        width: 8, height: 8, borderRadius: "50%", background: dot, position: "absolute",
        "@keyframes pulse": {
          "0%":   { boxShadow: `0 0 0 0 ${dot}66` },
          "70%":  { boxShadow: `0 0 0 6px ${dot}00` },
          "100%": { boxShadow: `0 0 0 0 ${dot}00`  },
        },
        animation: "pulse 2s infinite",
      }} />
    </Box>
  );
};

const AnimNumber: React.FC<{ value: number; duration?: number }> = ({ value, duration = 800 }) => {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = value;
    const startTime = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (t < 1) requestAnimationFrame(tick);
      else prev.current = end;
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <>{numFmt(display)}</>;
};

const ChartTip = ({ active, payload, label }: any) => {
  const { C, S } = useDashTokens();
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      background: C.headerBg, color: C.onDark, px: 2, py: 1.5, borderRadius: "10px",
      boxShadow: S.lg, border: "1px solid rgba(255,255,255,0.08)", fontFamily: F.mono,
    }}>
      <Typography sx={{ fontSize: 10, color: C.onDarkMuted, mb: 0.8, fontFamily: F.mono }}>{label}</Typography>
      {payload.map((p: any) => (
        <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: p.color }} />
          <Typography sx={{ fontSize: 11, fontFamily: F.mono, color: C.onDark }}>
            {p.name}: <strong>{numFmt(p.value)}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const SectionTitle: React.FC<{ title: string; sub?: string; right?: React.ReactNode }> = ({ title, sub, right }) => {
  const { C } = useDashTokens();
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
      <Box>
        <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 14, color: C.ink, lineHeight: 1.3 }}>{title}</Typography>
        {sub && <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted, mt: 0.3 }}>{sub}</Typography>}
      </Box>
      {right}
    </Box>
  );
};

const RiskBadge: React.FC<{ label: string; displayLabel?: string }> = ({ label, displayLabel }) => {
  const { C } = useDashTokens();
  return (
    <Chip label={displayLabel ?? label} size="small" sx={{
      bgcolor: riskBgClr(C, label), color: riskClr(C, label),
      fontFamily: F.mono, fontSize: 11, fontWeight: 700,
      height: 23, borderRadius: "5px",
      border: `1px solid ${riskClr(C, label)}30`,
    }} />
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("fr") ? frLocale : enLocale;
  const { mode, C, S, card, glow } = useDashTokens();
  const [data,            setData           ] = useState<any>(null);
  const [entryPointsData, setEntryPointsData] = useState<any[]>([]);
  const [loading,         setLoading        ] = useState(true);
  const [error,           setError          ] = useState<string | null>(null);
  // Dates default to null = no date filter = all-time totals
  const [startDate,    setStartDate   ] = useState<Date | null>(null);
  const [endDate,      setEndDate     ] = useState<Date | null>(null);
  const [selectedPort, setSelectedPort] = useState("ALL");
  const [selectedRisk, setSelectedRisk] = useState("ALL");

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const dateParams = {
        start_date: startDate ? format(startDate, "yyyy-MM-dd") : undefined,
        end_date:   endDate   ? format(endDate,   "yyyy-MM-dd") : undefined,
      };
      // Entry-points map is optional — don't blank the whole dashboard if that
      // route is missing on an older backend deploy.
      const [result, entryPointsResult] = await Promise.all([
        reportsAPI.getSituationRoom({
          ...dateParams,
          port:       selectedPort !== "ALL" ? selectedPort : undefined,
          risk_level: selectedRisk !== "ALL" ? selectedRisk : undefined,
        }),
        reportsAPI.getEntryPoints(dateParams).catch(() => ({ entry_points: [] })),
      ]);
      setData(result);
      setEntryPointsData(entryPointsResult?.entry_points || []);
    } catch (e: any) {
      setError(e?.message || t("ebolaDashboard.errors.loadFailed"));
    } finally { setLoading(false); }
  }, [startDate, endDate, selectedPort, selectedRisk, t]);

  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedPort("ALL");
    setSelectedRisk("ALL");
  };

  useEffect(() => {
    fetchData();
    const id = window.setInterval(fetchData, 30_000);
    return () => window.clearInterval(id);
  }, [fetchData]);

  const [feedback, setFeedback] = useState<any>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(true);

  const fetchFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    try {
      const result = await reportsAPI.getFeedback({
        start_date: startDate ? format(startDate, "yyyy-MM-dd") : undefined,
        end_date:   endDate   ? format(endDate,   "yyyy-MM-dd") : undefined,
      });
      setFeedback(result);
    } catch {
      // Non-critical widget — fail quietly, keep the rest of the dashboard usable
    } finally { setFeedbackLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchFeedback();
    const id = window.setInterval(fetchFeedback, 30_000);
    return () => window.clearInterval(id);
  }, [fetchFeedback]);

  const exportData = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([data.summary || {}]), "Summary");
    if (Array.isArray(data.ports))         XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.ports),          "Port of Entry");
    if (Array.isArray(data.latest_alerts)) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.latest_alerts), "Latest Alerts");
    XLSX.writeFile(wb, `ebola_dashboard_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
  };

  const summary = data?.summary || {};

  const tRisk = useCallback((raw: string = "") => {
    const l = raw.toLowerCase();
    if (l.includes("high")) return t("ebolaDashboard.riskLevels.highRisk");
    if (l.includes("mod"))  return t("ebolaDashboard.riskLevels.moderateRisk");
    if (l.includes("low") || l.includes("clear")) return t("ebolaDashboard.riskLevels.lowRisk");
    return raw;
  }, [t]);

  const portOptions = useMemo(() => {
    const s = new Set<string>();
    (data?.ports || []).forEach((p: any) => { const n = p?.port_name || p?.port; if (n) s.add(n); });
    return ["ALL", ...Array.from(s)];
  }, [data]);

  const trendData = useMemo(() =>
    (data?.screening_trend || []).map((i: any) => ({
      date: i.date,
      screenings:       i.screenings       || 0,
      high_risk_alerts: i.high_risk_alerts || 0,
      cleared:          i.cleared          || 0,
    })), [data]);

  // Keyed by the real `risk_level` values traveler_screenings actually stores
  // (binary: "cleared" | "high_risk", plus "temperature_pending" while a
  // screening awaits its final determination) — not display labels.
  const riskColors = useMemo<Record<string, string>>(() => ({
    "high_risk": C.red, "cleared": C.green, "temperature_pending": C.amber,
  }), [C]);

  const riskChartData = useMemo(() =>
    (data?.risk_distribution || []).map((i: any) => ({
      name: i.risk_level === "high_risk"
        ? t("ebolaDashboard.chartSeries.highRisk")
        : i.risk_level === "cleared"
          ? t("ebolaDashboard.chartSeries.cleared")
          : i.risk_level,
      value: i.count,
      color: riskColors[i.risk_level] || C.gray,
      raw: i.risk_level,
    })), [data, riskColors, C.gray, t]);

  const feedbackSummary = feedback?.summary || {};
  const netScore = feedbackSummary.net_score ?? 0;
  const netColor = netScore >= 0 ? C.green : C.red;
  const netLabel = netScore >= 50
    ? t("ebolaDashboard.sentiment.mostlyPositive")
    : netScore >= 0
      ? t("ebolaDashboard.sentiment.mixed")
      : t("ebolaDashboard.sentiment.needsAttention");

  const sentimentPieData = useMemo(() => ([
    { name: t("ebolaDashboard.sentiment.positive"), value: feedbackSummary.positive || 0, key: "Positive" },
    { name: t("ebolaDashboard.sentiment.neutral"),  value: feedbackSummary.neutral  || 0, key: "Neutral" },
    { name: t("ebolaDashboard.sentiment.negative"), value: feedbackSummary.negative || 0, key: "Negative" },
  ]), [feedbackSummary, t]);
  const sentimentColor = (key?: string) =>
    key === "Positive" || key === t("ebolaDashboard.sentiment.positive") ? C.green
      : key === "Neutral" || key === t("ebolaDashboard.sentiment.neutral") ? C.amber
      : C.red;

  const outcomeByPort = useMemo(() =>
    (data?.ports || []).slice(0, 6).map((p: any) => ({
      port:      (p.port_name || p.port || "").split(" ").slice(0, 2).join(" "),
      cleared:   p.cleared   || 0,
      high_risk: p.high_risk || 0,
      follow_up: p.suspected || 0,
    })), [data]);

  const countryData = useMemo(() => (data?.country_distribution || []).slice(0, 6), [data]);
  const symptomData = useMemo(() => (data?.symptom_distribution  || []).slice(0, 8), [data]);

  const mapPoints = useMemo(
    () =>
      (data?.country_distribution || []).map((item: any) => ({
        country: item.country,
        count: item.count,
        highRisk: item.high_risk_count || 0,
        risk: item.high_risk_count > 0 ? t("ebolaDashboard.riskLevels.highRisk") : t("ebolaDashboard.riskLevels.lowRisk"),
      })),
    [data, t]
  );

  const portTypeCounts = useMemo(() => {
    const counts = { Air: 0, Sea: 0, Land: 0 };
    (data?.ports || []).forEach((p: any) => {
      const t = (p.entry_type || "").toLowerCase();
      if (t.startsWith("air")) counts.Air++;
      else if (t.startsWith("sea")) counts.Sea++;
      else if (t.startsWith("land") || t.startsWith("rail")) counts.Land++;
    });
    return counts;
  }, [data]);

  // Sourced from GET /reports/entry-points (real entry_points table + traveler_screenings
  // counts), not data.ports — data.ports (situation-room) still feeds the other
  // port-of-entry panels (filter options, portTypeCounts, Outcome Distribution, table) unchanged.
  const portMapPoints = useMemo(() => entryPointsData, [entryPointsData]);

  const trendDelta = useMemo(() => {
    if (trendData.length < 2) return null;
    const last = trendData[trendData.length - 1].screenings;
    const prev = trendData[trendData.length - 2].screenings;
    if (!prev) return null;
    return Math.round(((last - prev) / prev) * 1000) / 10;
  }, [trendData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: "100vh", background: `linear-gradient(180deg, ${C.pageBgTop} 0%, ${C.pageBg} 320px)`, fontFamily: F.sans, transition: "background .3s ease" }}>

        {/* ══ HEADER ══════════════════════════════════════════════ */}
        <Box sx={{
          background: `linear-gradient(135deg, ${C.headerBg} 0%, ${C.headerBg2} 55%, ${C.headerBg} 100%)`,
          borderBottom: `1px solid ${C.headerBorder}`,
          position: "sticky", top: 0, zIndex: 100,
          boxShadow: "0 2px 24px rgba(0,0,0,0.3)",
        }}>
          <Box sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 0, display: "flex", alignItems: "stretch", minHeight: 64 }}>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 3, borderRight: `1px solid ${C.headerBorder}`, mr: 3 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                  <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 13, color: C.onDark, lineHeight: 1.2 }}>Voyage Santé+ DRC</Typography>
                  <Box component="span" sx={{ fontSize: 13, lineHeight: 1 }} aria-label="DR Congo flag" role="img">🇨🇩</Box>
                </Box>
                <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.onDarkMuted }}>{t("ebolaDashboard.brandMotto")}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 15, color: C.onDark, lineHeight: 1.2 }}>
                {t("ebolaDashboard.titleShort")}
              </Typography>
              <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.onDarkMuted, mt: 0.2 }}>
                {t("ebolaDashboard.subtitleLive")}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {[
                { icon: <Public sx={{ fontSize: 15, color: C.teal }} />, line1: "Ministère de la Santé Publique", line2: "(RDC MSP)" },
                { icon: <LocalHospital sx={{ fontSize: 15, color: C.teal }} />, line1: "INRB", line2: "Institut National de Recherche Biomédicale" },
              ].map((b, i) => (
                <Box key={i} sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 1.5, py: 0.8,
                  border: `1px solid ${C.headerBorder}`,
                  borderRadius: "8px",
                  background: "rgba(255,255,255,0.05)",
                  backdropFilter: "blur(6px)",
                  transition: "background .2s ease, border-color .2s ease",
                  "&:hover": { background: "rgba(255,255,255,0.08)", borderColor: "rgba(13,148,136,0.4)" },
                }}>
                  {b.icon}
                  <Box>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.onDark, lineHeight: 1.2 }}>{b.line1}</Typography>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.onDarkMuted, lineHeight: 1.2 }}>{b.line2}</Typography>
                  </Box>
                </Box>
              ))}

              <Box sx={{ px: 1.5, py: 0.8, border: `1px solid ${C.headerBorder}`, borderRadius: "8px", background: "rgba(255,255,255,0.04)" }}>
                <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.onDark, fontWeight: 600 }}>
                  {format(new Date(), "MMM dd, yyyy", { locale: dateLocale })}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.2 }}>
                  <LivePulse />
                  <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.onDarkMuted }}>
                    {format(new Date(), "HH:mm")} {t("ebolaDashboard.timezone")} &nbsp;·&nbsp; {t("ebolaDashboard.systemTime")}
                  </Typography>
                </Box>
              </Box>

            </Box>
          </Box>
        </Box>

        {/* ══ PAGE CONTENT ════════════════════════════════════════ */}
        <Box sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>

          {/* ── FILTER BAR ─────────────────────────────────────── */}
          <Box sx={{
            ...card(mode === "dark" ? { background: "rgba(17,26,44,0.75)", backdropFilter: "blur(10px)" } : {}),
            ...rise(0),
            px: 2.5, py: 1.8, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterList sx={{ fontSize: 15, color: C.muted }} />
              <Typography sx={{ fontFamily: F.sans, fontSize: 12, fontWeight: 600, color: C.sub }}>{t("ebolaDashboard.filters")}</Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <DatePicker
                label={t("ebolaDashboard.fromDate")}
                format="dd/MM/yyyy"
                value={startDate}
                maxDate={endDate ?? new Date()}
                onChange={(d) => setStartDate(d)}
                slotProps={{
                  field: { clearable: true },
                  textField: { size: "small", sx: { "& .MuiInputBase-root": { fontFamily: F.mono, fontSize: 12, borderRadius: "8px" } } },
                }}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <DatePicker
                label={t("ebolaDashboard.toDate")}
                format="dd/MM/yyyy"
                value={endDate}
                minDate={startDate ?? undefined}
                maxDate={new Date()}
                onChange={(d) => setEndDate(d)}
                slotProps={{
                  field: { clearable: true },
                  textField: { size: "small", sx: { "& .MuiInputBase-root": { fontFamily: F.mono, fontSize: 12, borderRadius: "8px" } } },
                }}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontFamily: F.mono, fontSize: 12 }}>{t("ebolaDashboard.portOfEntry")}</InputLabel>
              <Select value={selectedPort} label={t("ebolaDashboard.portOfEntry")} onChange={(e) => setSelectedPort(e.target.value)} sx={{ fontFamily: F.mono, fontSize: 12, borderRadius: "8px" }}>
                {portOptions.map((p) => (
                  <MenuItem key={p} value={p} sx={{ fontFamily: F.mono, fontSize: 12 }}>{p === "ALL" ? t("ebolaDashboard.allPorts") : p}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontFamily: F.mono, fontSize: 12 }}>{t("ebolaDashboard.riskLevel")}</InputLabel>
              <Select value={selectedRisk} label={t("ebolaDashboard.riskLevel")} onChange={(e) => setSelectedRisk(e.target.value)} sx={{ fontFamily: F.mono, fontSize: 12, borderRadius: "8px" }}>
                {/* Values are the backend risk_level codes stored in traveler_screenings */}
                {[
                  { value: "ALL",       label: t("ebolaDashboard.allRiskLevels") },
                  { value: "high_risk", label: t("ebolaDashboard.riskLevels.highRisk") },
                  { value: "cleared",   label: t("ebolaDashboard.clearedLow") },
                ].map((r) => (
                  <MenuItem key={r.value} value={r.value} sx={{ fontFamily: F.mono, fontSize: 12 }}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              <Button variant="outlined" size="small" onClick={resetFilters} startIcon={<Refresh sx={{ fontSize: 14 }} />}
                sx={{ fontFamily: F.sans, fontSize: 12, textTransform: "none", borderColor: C.cardBorder, color: C.sub, borderRadius: "8px", "&:hover": { borderColor: C.teal, color: C.teal, background: C.tealLt } }}>
                {t("ebolaDashboard.resetFilters")}
              </Button>
              <Button variant="contained" size="small" onClick={exportData} startIcon={<Download sx={{ fontSize: 14 }} />}
                sx={{ fontFamily: F.sans, fontSize: 12, textTransform: "none", background: `linear-gradient(135deg, ${C.teal}, ${C.tealDk})`, borderRadius: "8px", boxShadow: "0 2px 8px rgba(13,148,136,0.35)", "&:hover": { background: `linear-gradient(135deg, ${C.tealDk}, #085e58)` } }}>
                {t("ebolaDashboard.exportReport")}
              </Button>
            </Box>
          </Box>

          {/* ── KPI CARDS ───────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 2, ...rise(1) }}>
            {KPI_CONFIG.map((cfg) => {
              const IconComp = cfg.icon;
              const accent        = C[cfg.colorKey] as string;
              const periodVal     = summary[cfg.key] ?? 0;
              const cumulativeVal = (cfg as any).cumulative_key ? (summary[(cfg as any).cumulative_key] ?? 0) : null;
              const pct           = (cfg as any).pct_key ? summary[(cfg as any).pct_key] : null;
              const isPortCard    = cfg.key === "active_ports";
              const isAllTime     = !startDate && !endDate;
              const displayVal    = isAllTime && cumulativeVal != null ? cumulativeVal : periodVal;

              return (
                <Box key={cfg.key} sx={{
                  ...card(),
                  borderLeft: `3px solid ${accent}`,
                  p: 2.2,
                  "&:hover": { transform: "translateY(-3px)", boxShadow: S.xl },
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: "50%",
                      background: isPortCard ? C.divider : `${accent}1a`,
                      display: "grid", placeItems: "center", color: accent, flexShrink: 0,
                    }}>
                      <IconComp sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 13, color: C.ink }}>{t(cfg.labelKey)}</Typography>
                  </Box>

                  {isPortCard ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.4 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.sub }}>{t("ebolaDashboard.active")}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 20, fontWeight: 800, color: C.ink }}>
                          {loading ? <Skeleton width={40} /> : <AnimNumber value={periodVal} />}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "grid", gap: 0.9, mt: 0.5 }}>
                        {[
                          { label: t("ebolaDashboard.airport"),     count: portTypeCounts.Air,  color: C.blue },
                          { label: t("ebolaDashboard.seaport"),     count: portTypeCounts.Sea,  color: C.teal },
                          { label: t("ebolaDashboard.landBorder"), count: portTypeCounts.Land, color: C.amber },
                        ].map((row) => (
                          <Box key={row.label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: row.color, flexShrink: 0 }} />
                              <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.sub }}>{row.label}</Typography>
                            </Box>
                            <Typography sx={{ fontFamily: F.mono, fontSize: 13, fontWeight: 700, color: row.color }}>
                              {loading ? <Skeleton width={20} /> : row.count}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          <CalendarToday sx={{ fontSize: 12, color: C.muted }} />
                          <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.sub }}>{t("ebolaDashboard.cumulativeToDate")}</Typography>
                        </Box>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 18, fontWeight: 700, color: C.ink }}>
                          {loading ? <Skeleton width={40} /> : <AnimNumber value={cumulativeVal ?? 0} />}
                        </Typography>
                      </Box>

                      <Box sx={{ background: `${accent}12`, borderRadius: "12px", p: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.8 }}>
                          <FilterList sx={{ fontSize: 12, color: accent }} />
                          <Typography sx={{ fontFamily: F.mono, fontSize: 10.5, fontWeight: 600, color: accent }}>
                            {!startDate && !endDate
                              ? t("ebolaDashboard.allTime")
                              : `${startDate ? format(startDate, "MMM d", { locale: dateLocale }) : t("ebolaDashboard.start")} – ${endDate ? format(endDate, "MMM d, yyyy", { locale: dateLocale }) : t("ebolaDashboard.todayShort")}`}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 24, fontWeight: 800, color: accent, lineHeight: 1, textShadow: glow(accent) }}>
                          {loading ? <Skeleton width={50} /> : <AnimNumber value={displayVal} />}
                        </Typography>
                        {pct != null && (
                          <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: accent, opacity: 0.8, mt: 0.4 }}>
                            {t("ebolaDashboard.ofPeriodScreenings", { pct })}
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* ── MAPS ROW ────────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5, ...rise(2) }}>

            <Box sx={{ ...card(), overflow: "hidden", "&:hover": { boxShadow: S.md } }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.cardBorder}` }}>
                <SectionTitle title={t("ebolaDashboard.sections.screeningsByCountry")} />
              </Box>
              <Box sx={{ height: 480 }}>
                <GeoDashboardMap
                  mode={mode}
                  points={mapPoints}
                  colors={{
                    tooltipBg: C.headerBg,
                    tooltipText: C.onDark,
                    tooltipTextMuted: C.onDarkMuted,
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ ...card(), overflow: "hidden", "&:hover": { boxShadow: S.md } }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.cardBorder}` }}>
                <SectionTitle title={t("ebolaDashboard.sections.entryPointByVolume")} sub={t("ebolaDashboard.sections.airSeaLand")} />
              </Box>
              <Box sx={{ height: 480 }}>
                <PortEntryMap
                  mode={mode}
                  ports={portMapPoints}
                  colors={{
                    air: C.blue,
                    sea: C.teal,
                    land: C.amber,
                    tooltipBg: C.headerBg,
                    tooltipText: C.onDark,
                    tooltipTextMuted: C.onDarkMuted,
                  }}
                />
              </Box>
            </Box>

          </Box>

          {/* ── PORT TABLE (full width) ─────────────────────────── */}
          <Box sx={{ ...card(), display: "flex", flexDirection: "column", overflow: "hidden", "&:hover": { boxShadow: S.md }, ...rise(3) }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.cardBorder}` }}>
              <SectionTitle title={t("ebolaDashboard.sections.highRiskAlertsByPort")} />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "32px 1fr 100px 90px 100px 110px", px: 2.5, py: 1.3, background: C.pageBg, borderBottom: `1px solid ${C.cardBorder}`, gap: 1.5 }}>
              {[
                t("ebolaDashboard.table.number"),
                t("ebolaDashboard.table.portOfEntry"),
                t("ebolaDashboard.table.screened"),
                t("ebolaDashboard.table.cleared"),
                t("ebolaDashboard.table.highRisk"),
                t("ebolaDashboard.table.status"),
              ].map((h) => (
                <Typography key={h} sx={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.6 }}>{h}</Typography>
              ))}
            </Box>

            <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
              {loading && !data
                ? Array.from({ length: 7 }).map((_, i) => (
                    <Box key={i} sx={{ px: 2.5, py: 1.8, borderBottom: `1px solid ${C.divider}` }}>
                      <Skeleton height={22} />
                    </Box>
                  ))
                : (data?.ports || []).map((port: any, idx: number) => {
                    const name = port.port_name || port.port;
                    const st   = port.status    || t("ebolaDashboard.riskLevels.lowRisk");
                    return (
                      <Box key={name + idx} sx={{
                        display: "grid", gridTemplateColumns: "32px 1fr 100px 90px 100px 110px",
                        px: 2.5, py: 1.8, borderBottom: `1px solid ${C.divider}`,
                        gap: 1.5, alignItems: "center",
                        transition: "background .15s",
                        "&:hover": { background: C.pageBg },
                      }}>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 12.5, color: C.muted }}>{idx + 1}</Typography>
                        <Typography sx={{ fontFamily: F.sans, fontSize: 14, fontWeight: 600, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 14, color: C.ink }}>{numFmt(port.screened)}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 14, color: C.green, fontWeight: 700 }}>{numFmt(port.cleared)}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 14, color: C.red, fontWeight: 700 }}>{numFmt(port.high_risk)}</Typography>
                        <RiskBadge label={st} displayLabel={tRisk(st)} />
                      </Box>
                    );
                  })
              }

              {!loading && data?.ports && (
                <Box sx={{ display: "grid", gridTemplateColumns: "32px 1fr 100px 90px 100px 110px", px: 2.5, py: 1.8, background: C.tableTotalBg, borderTop: `2px solid ${C.cardBorder}`, gap: 1.5, alignItems: "center" }}>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.muted }}>∑</Typography>
                  <Typography sx={{ fontFamily: F.sans, fontSize: 14, fontWeight: 700, color: C.ink }}>{t("ebolaDashboard.table.total")}</Typography>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 14, fontWeight: 700, color: C.ink }}>{numFmt(summary.total_screenings)}</Typography>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 14, fontWeight: 700, color: C.green }}>{numFmt(summary.cleared)}</Typography>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 14, fontWeight: 700, color: C.red }}>{numFmt(summary.high_risk)}</Typography>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 13, color: C.muted }}>—</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* ── CHARTS ROW ──────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1fr", gap: 2.5, ...rise(4) }}>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title={t("ebolaDashboard.sections.screeningsTrend")} sub={t("ebolaDashboard.sections.screeningsTrendSub")}
                right={trendDelta != null && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.4, borderRadius: "20px", background: trendDelta >= 0 ? C.greenLt : C.redLt }}>
                    {trendDelta >= 0 ? <TrendingUp sx={{ fontSize: 12, color: C.green }} /> : <TrendingDown sx={{ fontSize: 12, color: C.red }} />}
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: trendDelta >= 0 ? C.green : C.red, fontWeight: 700 }}>
                      {trendDelta >= 0 ? "+" : ""}{trendDelta}%
                    </Typography>
                  </Box>
                )}
              />
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={trendData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gBlue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.blue} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={C.blue} stopOpacity={0}    />
                    </linearGradient>
                    <linearGradient id="gRed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.red} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C.red} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.divider} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTip />} cursor={{ stroke: C.cardBorder }} />
                  <Area type="monotone" dataKey="screenings"       name={t("ebolaDashboard.chartSeries.totalScreenings")} stroke={C.blue} strokeWidth={2.5} fill="url(#gBlue)" dot={false} />
                  <Area type="monotone" dataKey="high_risk_alerts" name={t("ebolaDashboard.chartSeries.highRiskAlerts")} stroke={C.red}  strokeWidth={2}   fill="url(#gRed)"  dot={false} strokeDasharray="4 2" />
                  <Legend wrapperStyle={{ fontFamily: F.mono, fontSize: 10, paddingTop: 8 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title={t("ebolaDashboard.sections.outcomeDistribution")} />
              <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                {[
                  { c: C.green, l: t("ebolaDashboard.chartSeries.passedCleared") },
                  { c: C.red,   l: t("ebolaDashboard.chartSeries.highRiskAlerts") },
                  { c: C.amber, l: t("ebolaDashboard.chartSeries.referredFollowup") },
                ].map((item) => (
                  <Box key={item.l} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: item.c }} />
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.sub }}>{item.l}</Typography>
                  </Box>
                ))}
              </Box>
              <ResponsiveContainer width="100%" height={215}>
                <ReBarChart data={outcomeByPort} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="port" tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} width={80} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: C.cardHover }} />
                  <Bar dataKey="cleared"   name={t("ebolaDashboard.chartSeries.cleared")}   stackId="a" fill={C.green} />
                  <Bar dataKey="high_risk" name={t("ebolaDashboard.chartSeries.highRisk")} stackId="a" fill={C.red}   />
                  <Bar dataKey="follow_up" name={t("ebolaDashboard.chartSeries.followUp")} stackId="a" fill={C.amber} radius={[0, 4, 4, 0]} />
                </ReBarChart>
              </ResponsiveContainer>
              <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted, mt: 1, textAlign: "center" }}>{t("ebolaDashboard.sections.percentageOfTotal")}</Typography>
            </Box>

            <Box sx={{ ...card(), p: 2.5, display: "flex", flexDirection: "column" }}>
              <SectionTitle title={t("ebolaDashboard.sections.riskLevelSummary")} right={
                <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.muted }}>{t("ebolaDashboard.totalCount", { count: summary.total_screenings ?? 0 })}</Typography>
              } />
              <Box sx={{ flex: 1, position: "relative" }}>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie data={riskChartData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={3} strokeWidth={0}>
                      {riskChartData.map((e: any) => <Cell key={e.name} fill={e.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none" }}>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 24, fontWeight: 800, color: C.ink, lineHeight: 1 }}>
                    {numFmt(summary.total_screenings || 0)}
                  </Typography>
                  <Typography sx={{ fontFamily: F.sans, fontSize: 10, color: C.muted }}>{t("common.total")}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "grid", gap: 1, mt: 1 }}>
                {[
                  { label: t("ebolaDashboard.chartSeries.cleared"),   color: C.green, valKey: "cleared"   },
                  { label: t("ebolaDashboard.chartSeries.highRisk"), color: C.red,   valKey: "high_risk" },
                ].map((item) => {
                  const v  = summary[item.valKey] ?? riskChartData.find((r: any) => r.raw === item.valKey)?.value ?? 0;
                  const pc = summary.total_screenings ? Math.round((v / summary.total_screenings) * 1000) / 10 : 0;
                  return (
                    <Box key={item.label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: item.color }} />
                        <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.ink }}>{item.label}</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.sub }}>
                        {numFmt(v)} <span style={{ color: C.muted }}>({pc}%)</span>
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* ── ANALYTICS ROW ───────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1fr", gap: 2.5, ...rise(5) }}>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title={t("ebolaDashboard.sections.countryAnalysis")} />
              <Box sx={{ display: "grid", gap: 1.4 }}>
                {(countryData || []).map((item: any, idx: number) => {
                  const palette = [C.blue, C.teal, C.purple, C.amber, C.red, C.green];
                  const pct = summary.total_screenings ? Math.round((item.count / summary.total_screenings) * 100) : 0;
                  return (
                    <Box key={item.country || idx}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
                        <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.ink }}>{item.country || t("common.unknown")}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.sub, fontWeight: 600 }}>{numFmt(item.count)}</Typography>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 3, background: C.divider, overflow: "hidden" }}>
                        <Box sx={{ height: "100%", borderRadius: 3, background: palette[idx % palette.length], width: `${pct}%`, transition: "width 1s cubic-bezier(.4,0,.2,1)" }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title={t("ebolaDashboard.sections.symptomsDistribution")} />
              <ResponsiveContainer width="100%" height={240}>
                <ReBarChart data={symptomData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gSymp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={C.blue} stopOpacity={1}   />
                      <stop offset="100%" stopColor={C.teal} stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.divider} vertical={false} />
                  <XAxis dataKey="symptom" tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: C.cardHover }} />
                  <Bar dataKey="count" name={t("ebolaDashboard.chartSeries.cases")} fill="url(#gSymp)" radius={[5, 5, 0, 0]} />
                </ReBarChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ ...card(), p: 2.5, display: "flex", flexDirection: "column" }}>
              <SectionTitle title={t("ebolaDashboard.sections.feedbackSentiment")} sub={t("ebolaDashboard.sections.whatsappAssistant")} />
              {feedbackLoading ? (
                <Skeleton variant="rounded" height={190} />
              ) : !feedbackSummary.total ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 190 }}>
                  <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.muted }}>{t("ebolaDashboard.sections.noFeedbackYet")}</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ flex: 1, position: "relative" }}>
                    <ResponsiveContainer width="100%" height={190}>
                      <PieChart>
                        <Pie data={sentimentPieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={88} paddingAngle={4} strokeWidth={0}>
                          {sentimentPieData.map((e: any) => (
                            <Cell key={e.name} fill={sentimentColor(e.key)} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none" }}>
                      <Typography sx={{ fontFamily: F.mono, fontSize: 20, fontWeight: 800, color: netColor, lineHeight: 1 }}>
                        {netScore > 0 ? "+" : ""}{netScore}
                      </Typography>
                      <Typography sx={{ fontFamily: F.sans, fontSize: 10, color: C.muted }}>{netLabel}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "grid", gap: 0.8 }}>
                    {sentimentPieData.map((item: any) => (
                      <Box key={item.name} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: sentimentColor(item.key) }} />
                          <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.ink }}>{item.name}</Typography>
                        </Box>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.sub, fontWeight: 600 }}>{numFmt(item.value)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {error && (
            <Box sx={{ ...card(), p: 2.5, borderLeft: `4px solid ${C.red}`, background: C.redLt }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ErrorOutline sx={{ fontSize: 18, color: C.red }} />
                <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.red }}>{error}</Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ height: 20 }} />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardPage;
